import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { MapPin, Pencil, Check, Clock, Link2, Copy } from 'lucide-react';

export const ClientDeliveryPoints = ({ clientId }) => {
  const [points, setPoints] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}/delivery-points`, { headers: getAuthHeaders() });
      setPoints(r.data || []);
    } catch { /* точек может не быть */ }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      label: p.label || '', address_line: p.address_line || '', city: p.city || '',
      contact_person: p.contact_person || '', phone: p.phone || '',
      open_time: p.open_time || '', close_time: p.close_time || '', driver_note: p.driver_note || '',
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/delivery-points/${editing}`, form, { headers: getAuthHeaders() });
      toast.success('Точка сохранена');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось сохранить');
    }
    setSaving(false);
  };

  const share = async (p) => {
    try {
      const r = await axios.post(`${API}/admin/delivery-points/${p.id}/share`, {}, { headers: getAuthHeaders() });
      const url = r.data.share_url;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success('Ссылка для водителя скопирована');
      setPoints((prev) => prev.map((x) => (x.id === p.id ? { ...x, share_url: url } : x)));
    } catch { toast.error('Не удалось создать ссылку'); }
  };

  return (
    <div className="border rounded-lg p-3 bg-emerald-50/40" data-testid="client-delivery-points">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 font-medium text-sm">
          <MapPin className="w-4 h-4 text-emerald-700" /> Точки доставки
        </div>
        <Badge variant="outline">{points.length}</Badge>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Время работы, контакт и заметку видит водитель по ссылке. БИН, юр.адрес и цены в ссылку не попадают.
      </p>

      {points.length === 0 && <p className="text-sm text-gray-500">Точек пока нет — клиент добавляет их в профиле</p>}

      <div className="space-y-2">
        {points.map((p) => (
          <div key={p.id} className="bg-white border rounded-md p-2.5 text-sm">
            {editing === p.id ? (
              <div className="space-y-2" data-testid="delivery-point-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Название точки</Label>
                    <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                           data-testid="point-input-label" />
                  </div>
                  <div>
                    <Label className="text-xs">Город</Label>
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Адрес</Label>
                  <Input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Открытие (ЧЧ:ММ)</Label>
                    <Input placeholder="09:00" value={form.open_time}
                           onChange={(e) => setForm({ ...form, open_time: e.target.value })}
                           data-testid="point-input-open" />
                  </div>
                  <div>
                    <Label className="text-xs">Закрытие (ЧЧ:ММ)</Label>
                    <Input placeholder="21:00" value={form.close_time}
                           onChange={(e) => setForm({ ...form, close_time: e.target.value })}
                           data-testid="point-input-close" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Контактное лицо</Label>
                    <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Телефон точки</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Заметка водителю</Label>
                  <Input placeholder="вход со двора, звонить за 10 мин"
                         value={form.driver_note}
                         onChange={(e) => setForm({ ...form, driver_note: e.target.value })}
                         data-testid="point-input-note" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={save} disabled={saving} data-testid="point-save-btn">
                    <Check className="w-3.5 h-3.5 mr-1" /> Сохранить
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Отмена</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {p.label || 'Без названия'}
                    {p.is_default && <Badge className="bg-emerald-600">основная</Badge>}
                  </div>
                  <div className="text-xs text-gray-600">{p.address_line}{p.city ? `, ${p.city}` : ''}</div>
                  <div className="text-xs text-gray-600 flex items-center gap-2 flex-wrap mt-0.5">
                    {(p.open_time || p.close_time) && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />{p.open_time || '—'}–{p.close_time || '—'}
                      </span>
                    )}
                    {p.contact_person && <span>{p.contact_person}</span>}
                    {p.phone && <span>{p.phone}</span>}
                  </div>
                  {p.driver_note && <div className="text-xs text-amber-700 mt-0.5">{p.driver_note}</div>}
                  {p.share_url && (
                    <div className="text-xs text-gray-400 mt-0.5 truncate" data-testid="point-share-url">{p.share_url}</div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => share(p)}
                          title="Ссылка для водителя" data-testid={`point-share-${p.id}`}>
                    {p.share_url ? <Copy className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(p)}
                          data-testid={`point-edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
